import React, {useEffect} from 'react';
import {Text, Icon} from '@/ui-kits';
import {useSelector, useDispatch} from 'react-redux';
import {notifications, totalUnreadNotifications, notificationStatus} from '@/services/redux/selectors';
import {getNotificationsRequest, markAllAsReadRequest} from '@/services/redux/slices';
import {cn, formatDate} from '@/utils/common_functions';

interface NotificationModalProps {
  open: boolean;
  onClose: () => void;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({open, onClose}) => {
  const dispatch = useDispatch();
  const notificationList = useSelector(notifications);
  const unreadCount = useSelector(totalUnreadNotifications);
  const isLoading = useSelector(notificationStatus);

  // Fetch notifications when modal opens
  useEffect(() => {
    if (open) {
      dispatch(getNotificationsRequest({page: 1}));
    }
  }, [open, dispatch]);


  const handleMarkAllRead = () => {
    if (unreadCount > 0) {
      dispatch(markAllAsReadRequest());
    }
  };

  const handleNotificationClick = (notificationId: number) => {
    dispatch(markAllAsReadRequest({ id: notificationId }));
  };

  if (!open) return null;

  return (
    <div
  className="
    absolute
    top-full
    right-0
    mt-2
    bg-white
    rounded-lg
    shadow-lg
    border border-ui_input_border
    w-full
    max-w-[450px]
    min-w-[260px]
    max-h-[calc(100vh-8rem)]
    flex flex-col
    z-[9999]
    overflow-hidden
    sm:w-[350px]
    md:w-[450px]
  "
  style={{ maxWidth: 'calc(100vw - 16px)' }}
  onClick={(e) => e.stopPropagation()}
>

      {/* Header */}
      <div className="flex items-center justify-between px-2 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-ui_input_border flex-shrink-0 gap-1 sm:gap-2">
        <Text variant='body' className="!font-FigtreeSemiBold !text-base sm:!text-xl md:!text-2xl text-deep_blue truncate flex-1 min-w-0">
          Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </Text>
        <button 
          className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          onClick={handleMarkAllRead}>
          <Icon type="mark_all_read" className="size-3 sm:size-4 md:size-5 flex-shrink-0" />
          <Text variant='body' className="!font-FigtreeRegular text-[10px] sm:text-sm md:text-base text-deep_blue whitespace-nowrap">
            Mark all read
          </Text>
        </button>
      </div>

      {/* Notification List */}
      <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Text className="text-ui_blue">Loading...</Text>
          </div>
        ) : notificationList.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Text className="text-ui_blue">No notifications</Text>
          </div>
        ) : (
          <div className="flex flex-col">
            {notificationList.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification.id)}
                className={cn(
                  'px-2 sm:px-4 py-2 sm:py-3 border-b border-ui_input_border last:border-b-0 transition-colors min-w-0 w-full cursor-pointer hover:opacity-80',
                  !notification.is_read ? 'bg-profile_bg_light_blue/40' : 'bg-white'
                )}>
                <Text variant='body' className="!font-FigtreeBold !text-md !sm:text-md text-deep_blue mb-1 break-words whitespace-normal">
                  {notification.title}
                </Text>
                <Text variant='body' className="!font-FigtreeRegular !text-xs sm:!text-sm md:!text-sm text-deep_blue mb-1  break-words whitespace-normal">
                  {notification.message}
                </Text>
                <Text variant='small' className="!font-FigtreeMeduim !text-[10px] sm:!text-xs md:!text-xs text-ui_placeholder whitespace-nowrap">
                  {formatDate(notification.created_at, 'dd-MMM-yyyy, HH:mm:ss')}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

